import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from './authSlice';

export const ACTIVE_LOCATION_KEY = 'qo.location';

/** The shell's context switcher: restaurant (Admin only) and location. null location = all locations. */
export interface ActiveLocationState {
    restaurantId: string | null;
    locationId: string | null;
}

export interface ContextOption {
    id: string;
    name: string;
}

interface State extends ActiveLocationState {
    restaurants: ContextOption[];
    locations: ContextOption[];
}

const readStored = (): ActiveLocationState => {
    try {
        const raw = localStorage.getItem(ACTIVE_LOCATION_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === 'object') {
            const { restaurantId, locationId } = parsed as Partial<ActiveLocationState>;
            return {
                restaurantId: typeof restaurantId === 'string' ? restaurantId : null,
                locationId: typeof locationId === 'string' ? locationId : null,
            };
        }
    } catch {
        // corrupt value: fall through to the empty selection
    }
    return { restaurantId: null, locationId: null };
};

const persist = (state: ActiveLocationState) =>
    localStorage.setItem(
        ACTIVE_LOCATION_KEY,
        JSON.stringify({ restaurantId: state.restaurantId, locationId: state.locationId })
    );

const initialState: State = { ...readStored(), restaurants: [], locations: [] };

const activeLocationSlice = createSlice({
    name: 'activeLocation',
    initialState,
    reducers: {
        setRestaurants: (state, action: PayloadAction<ContextOption[]>) => {
            state.restaurants = action.payload;
        },
        setLocationOptions: (state, action: PayloadAction<ContextOption[]>) => {
            state.locations = action.payload;
        },
        selectRestaurant: (state, action: PayloadAction<string | null>) => {
            state.restaurantId = action.payload;
            state.locationId = null;
            state.locations = [];
            persist(state);
        },
        selectLocation: (state, action: PayloadAction<string | null>) => {
            state.locationId = action.payload;
            persist(state);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => {
            localStorage.removeItem(ACTIVE_LOCATION_KEY);
            return { restaurantId: null, locationId: null, restaurants: [], locations: [] };
        });
    },
});

export const { setRestaurants, setLocationOptions, selectRestaurant, selectLocation } = activeLocationSlice.actions;
export default activeLocationSlice.reducer;
